export function createRealtimeSessionId (prefix = 'session') {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function') {
    return `${prefix}-${globalThis.crypto.randomUUID()}`
  }

  return `${prefix}-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

export function countPresence (state) {
  return Object.values(state || {}).reduce((count, value) => {
    return count + (Array.isArray(value) ? value.length : 0)
  }, 0)
}

export function flattenPresence (state) {
  return Object.entries(state || {}).flatMap(([key, items]) => {
    const metas = Array.isArray(items) ? items : []
    return metas.map(meta => ({
      key,
      ...meta
    }))
  })
}

export function toFirestoreLikeTimestamp (value) {
  const date = value ? new Date(value) : new Date()

  return {
    toDate: () => date,
    toMillis: () => date.getTime()
  }
}

export async function isPlaybackLive (url) {
  if (!url) return false

  try {
    const response = await fetch(url + (url.includes('?') ? '&' : '?') + `t=${Date.now()}`, {
      method: 'HEAD',
      cache: 'no-store'
    })

    return response.ok
  } catch {
    return false
  }
}

export function mapChatMessage (row) {
  if (!row) return null

  return {
    id: row.id,
    user_id: row.user_id,
    text: row.text,
    username: row.username,
    email: row.email,
    avatar: row.avatar,
    exp: row.exp,
    badges: Array.isArray(row.badges) ? row.badges : [],
    clientId: row.client_id,
    created_at: row.created_at,
    timestamp: toFirestoreLikeTimestamp(row.created_at)
  }
}

export function mapLiveRow (row) {
  if (!row) return null

  return {
    id: row.old_id || row.id,
    provider: row.provider,
    poster: row.poster,
    posterType: row.poster_type || 'image',
    streamKey: row.stream_key,
    playbackUrl: row.playback_url,
    streamLive: Boolean(row.stream_live),
    isOpen: Boolean(row.is_open),
    preparing: Boolean(row.preparing),
    isFinished: Boolean(row.is_finished),
    showOfflineMessage: Boolean(row.show_offline_message),
    showMembersCount: row.show_members_count !== false,
    recordTimestamp: row.record_timestamp
  }
}

export function getLiveRealtimeIds (live = {}) {
  return [...new Set([
    live?.stream_id,
    live?.old_key,
    live?.rtmp_server_stream_key
  ].filter(Boolean).map(String))]
}

function isMissingColumnError (error, column) {
  const message = [error?.message, error?.details, error?.hint].filter(Boolean).join(' ')

  return (
    (error?.code === '42703' || error?.code === 'PGRST204') &&
    message.includes(column)
  )
}

function isUuidLike (value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || '')
  )
}

export async function fetchLatestLiveRow (client, ids, select = '*') {
  const normalizedIds = [...new Set((ids || []).filter(Boolean).map(String))]
  if (!client || !normalizedIds.length) return null

  const selectLatest = async (column) => {
    const values = column === 'id' ? normalizedIds.filter(isUuidLike) : normalizedIds

    if (!values.length) {
      return {data: [], error: null}
    }

    return client
      .from('public_lives')
      .select(select)
      .in(column, values)
      .order('updated_at', { ascending: false })
      .limit(1)
  }

  for (const column of ['old_id', 'id', 'stream_key']) {
    const response = await selectLatest(column)

    if (response.error && isMissingColumnError(response.error, column)) {
      continue
    }

    if (response.error) {
      throw response.error
    }

    if (response.data?.[0]) {
      return response.data[0]
    }
  }

  return null
}

export async function ensurePublicLiveExists ({ client, ids, projectId } = {}) {
  if (!client || !ids?.length || !projectId) return null

  const existing = await fetchLatestLiveRow(client, ids)

  if (existing) {
    return { row: existing, created: false }
  }

  const streamKey = ids[0] || createRealtimeSessionId('live')
  const oldId = ids.find(id => id !== streamKey) || streamKey

  const { data, error } = await client
    .from('public_lives')
    .insert({
      old_id: oldId,
      stream_key: streamKey,
      project_id: projectId ? Number(projectId) : null,
      provider: 'll-hls',
      poster: '',
      poster_type: 'image',
      playback_url: streamKey ? `https://hls.estage-oms.com/app/${streamKey}/abr.m3u8` : '',
      stream_live: false,
      is_open: false,
      preparing: false,
      is_finished: false,
      show_offline_message: false,
      show_members_count: true
    })
    .select('*')
    .limit(1)
    .maybeSingle()

  if (error) {
    console.warn('[supabase-realtime] Failed to create public_lives record', error)
    return null
  }

  return { row: data, created: true }
}

export function subscribeToLiveState ({ client, ids, onUpdate, onError } = {}) {
  const normalizedIds = [...new Set((ids || []).filter(Boolean).map(String))]

  if (!client || !normalizedIds.length || typeof onUpdate !== 'function') {
    return () => {}
  }

  let active = true
  const channel = client.channel(`live-state:${normalizedIds.join(':')}:${Date.now()}`)

  const handleRow = (row) => {
    if (!active || !row) return

    const rowIds = [row.old_id, row.id, row.stream_key].filter(Boolean).map(String)

    if (!rowIds.some(id => normalizedIds.includes(id))) {
      return
    }

    onUpdate(mapLiveRow(row), row)
  }

  fetchLatestLiveRow(client, normalizedIds)
    .then(row => {
      handleRow(row)
    })
    .catch(error => {
      onError?.(error)
    })

  channel
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'public_lives'
    }, payload => {
      handleRow(payload?.new || payload?.old)
    })
    .subscribe(status => {
      if (status === 'CHANNEL_ERROR') {
        onError?.(new Error('Live state realtime subscription failed'))
      }
    })

  return () => {
    active = false
    client.removeChannel(channel)
  }
}
